import React, { useState } from 'react';

import { useNavigate } from 'react-router-dom';

import CircularProgress from '@mui/material/CircularProgress';
import { AttachFile, RotateRight, Undo } from '@mui/icons-material';

import MediasService from '../../../services/MediasService';

import CommonButton from '../../buttons/common-button';
import InfoBox from '../../blocks/messages/info-box';

import { FileOpener } from '@capacitor-community/file-opener'

import { Filesystem, Directory } from '@capacitor/filesystem';
import { Capacitor } from '@capacitor/core';

/**
 * GallerySection Component
 * 
 * This component renders a gallery of media files from a folder.
 * @param {object} folder - The folder to fetch media files from
 * @param {boolean} inAppointment - Optional prop to indicate if the upload is in an appointment context
 * 
 * @returns {JSX.Element}
 */
const GallerySection = ({
    folder,
    inAppointment = false, // Optional prop to indicate if the upload is in an appointment context
}) => {
    // State variable for gallery media
    const [galleryMedia, setGalleryMedia] = useState([]);

    // State variables for loading and error handling
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // State for the zoomed-in media
    const [zoomedMedia, setZoomedMedia] = useState(null);

    // Hook for navigation
    const navigate = useNavigate();

    /**
     * Fetches the media files from the folder.
     * Sets the loading state and handles errors.
     * @returns {Promise<void>} A promise that resolves when the data is fetched.
     * @throws {Error} Throws an error if fetching fails
     */
    const fetchGalleryMedia = async () => {
        try {
            // Set loading state to true
            setLoading(true);

            // Fetch media files from the folder
            const media = await MediasService.getMediaByFolderId(folder.id);

            // Update the gallery media state with the fetched media
            setGalleryMedia(media);
        } catch (error) {
            setError(error?.message || 'Une erreur inattendue est survenue');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Renders the media file based on its type.
     * @param {string} media - The media file to render
     * @returns {JSX.Element} The rendered media file
     */
    const renderMedia = (media, index) => {
        // Check if the media is an image or a video
        const mediaData = media.file_data;

        const handleClick = () => {
            setZoomedMedia(media); // Set the clicked media for zooming
        };

        if (mediaData.startsWith('data:image')) {
            // Display an image if the media is an image
            return (
                <img
                    key={`${media.file_name}_${index}`}
                    src={mediaData}
                    alt={`Média_${index}`}
                    onClick={handleClick}
                    style={{ cursor: 'pointer' }}
                />
            );
        } else if (mediaData.startsWith('data:video')) {
            // Display a video if the media is a video
            return (
                <video
                    key={`${media.file_name}_${index}`}
                    controls
                    style={{ cursor: 'pointer' }}
                >
                    <source src={mediaData} type="video/mp4" />
                    Votre navigateur ne supporte pas la lecture des vidéos.
                </video>
            );
        } else {
            return (
                <div
                    key={`${media.file_name}_${index}`}
                    className='media-file-item'
                    onClick={() => handleDownloadFile(media)}
                >
                    <AttachFile />
                    <span className='file-name'>{media.file_name}</span>
                </div>
            );
        }
    };

    // Close zoomed-in media
    const closeZoom = () => {
        setZoomedMedia(null);
    };

    /**
     * Get the MIME type based on the file extension.
     * @param {string} fileName - The name of the file
     * @returns {string} The MIME type of the file
     */
    const getMimeType = (fileName) => {
        const ext = fileName.split('.').pop().toLowerCase();
        switch (ext) {
            case 'pdf':
                return 'application/pdf';
            case 'docx':
                return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
            case 'doc':
                return 'application/msword';
            case 'xlsx':
                return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
            case 'xls':
                return 'application/vnd.ms-excel';
            case 'pptx':
                return 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
            case 'txt':
                return 'text/plain';
            default:
                return '*/*'; // fallback
        }
    };
    

    // Handle file download
    const handleDownloadFile = async (media) => {
        const mediaData = media.file_data;
        const fileName = media.file_name;

        if (!mediaData.startsWith('data:')) {
            console.error("Format de données invalide.");
            return;
        }

        try {
            const base64Data = mediaData.split(',')[1]; // Extract the base64 data
            const mimeType = getMimeType(fileName); // Get the MIME type

            if (Capacitor.isNativePlatform()) {
                // download the file to the device
                const filePath = `${fileName}`;

                try {
                    const res = await Filesystem.writeFile({
                        path: filePath,
                        data: base64Data,
                        directory: Directory.Documents,
                    });

                    // Check if the file was written successfully
                    if (!res.uri) {
                        throw new Error("Erreur lors de l'écriture du fichier");
                    }
    
                    // Open the file using FileOpener
                    const fileUri = await Filesystem.getUri({
                        path: filePath,
                        directory: Directory.Documents,
                    });

                    // Check if the file URI was retrieved successfully
                    if (!fileUri.uri) {
                        throw new Error("Erreur lors de la récupération de l'URI du fichier");
                    }
    
                    // Open the file using FileOpener
                    await FileOpener.open({
                        filePath: fileUri.uri,
                        contentType: mimeType,
                    });
                } catch (error) {
                    console.error('Erreur mobile :', error);
                }
            } else {
                // Create a Blob from the base64 data
                const byteString = atob(base64Data);
                const arrayBuffer = new ArrayBuffer(byteString.length);
                const view = new Uint8Array(arrayBuffer);
                for (let i = 0; i < byteString.length; i++) {
                    view[i] = byteString.charCodeAt(i);
                }

                const blob = new Blob([arrayBuffer], { type: mimeType });
                const objectURL = URL.createObjectURL(blob);

                const link = document.createElement('a');
                link.href = objectURL;
                link.download = fileName;
                link.click();

                // Clean up the object URL after 1 second
                setTimeout(() => URL.revokeObjectURL(objectURL), 1000);
            }
        } catch (error) {
            console.error("Erreur lors du traitement du fichier :", error);
        }
    };

    // Fetch media files on component mount
    return (
        <div className="media-gallery--section">
            {/* Display a loading spinner if loading is true */}
            {loading && <CircularProgress />}

            {/* Display an error message if an error occurs */}
            {error && <InfoBox type="error" text={error} />}

            <div className="gallery-media">
                {/* Display a message if no media is found */}
                {galleryMedia.length === 0 && !loading && (
                    <InfoBox
                        type="novelty"
                        text={
                            <p>Pour des raisons de performance, les médias ne sont pas chargés automatiquement. Veuillez cliquer sur le bouton "Charger les médias" pour afficher les médias du dossier.</p>
                        }
                        disabledCloseBtn={true}
                    />
                )}

                {/* Map through the media array and render images or videos */}
                {galleryMedia.map((media, index) => renderMedia(media, index))}
            </div>

            <div className='gallery-actions'>
                {/* Display a button to fetch media files */}
                <CommonButton
                    icon={<RotateRight />}
                    label="Voir les médias existants"
                    onClick={fetchGalleryMedia}
                />

                {/* Display the form with the fields and submit buttons */}
                { !inAppointment && (
                    <CommonButton
                        icon={<Undo />}
                        label={'Retour'}
                        onClick={() => navigate(-1)} 
                    />
                )}
            </div>

            {/* Modal for zoomed-in media */}
            {zoomedMedia && (
                <div className="zoom-modal" onClick={closeZoom}>
                    <div className="zoom-modal-content" onClick={(e) => e.stopPropagation()}>
                        {zoomedMedia.file_data.startsWith('data:image') && (
                            <img src={zoomedMedia.file_data} alt="Zoomed" />
                        )}
                        <button className="close-zoom-btn" onClick={closeZoom}>Fermer</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GallerySection;
